import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../AuthContext";
import { ShieldAlert, ShieldCheck, RefreshCw, BellRing, BellOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SEV = {
  high: { badge: "bg-rose-500/12 text-rose-500", bar: "bg-rose-500" },
  medium: { badge: "bg-amber-500/12 text-amber-500", bar: "bg-amber-500" },
  low: { badge: "bg-sky-500/12 text-sky-500", bar: "bg-sky-500" },
};

export default function AnomalyReport({ dm }) {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  const alertsOn = !!data?.alerts?.enabled;

  const toggleAlerts = async () => {
    setToggling(true);
    try {
      const r = await axios.put(`${API}/api/admin/system/anomaly-alerts`,
        { enabled: !alertsOn }, { headers: authHeader() });
      setData(d => ({ ...d, alerts: r.data.alerts }));
      toast.success(r.data.alerts.enabled
        ? "MC/Discord alerts ON — high anomalies will be forwarded (24h dedup)"
        : "MC/Discord alerts OFF — anomalies stay admin-only");
    } catch (e) {
      toast.error("Failed to update alert flag");
    } finally { setToggling(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/system/anomalies`, { headers: authHeader() });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load anomaly report");
    } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const anomalies = data?.anomalies || [];
  const clean = data && anomalies.length === 0;

  return (
    <div data-testid="anomaly-report" className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          {clean
            ? <ShieldCheck className="w-4 h-4 text-emerald-500" />
            : <ShieldAlert className={`w-4 h-4 ${anomalies.some(a => a.severity === "high") ? "text-rose-500" : "text-amber-500"}`} />}
          <h3 className={`editorial-heading text-xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Anomaly Report</h3>
          <span className={`category-tag px-2 py-0.5 rounded-full ${dm ? "bg-white/5 text-slate-400" : "bg-black/5 text-[#6b7a7d]"}`}>last 24h</span>
          {data?.environment && (
            <span data-testid="anomaly-env-badge"
              title={data.environment === "preview" ? "Development environment — these anomalies do NOT reflect production status" : "Live production environment"}
              className={`category-tag px-2 py-0.5 rounded-full font-semibold ${data.environment === "preview" ? "bg-amber-500/15 text-amber-500" : "bg-emerald-500/10 text-emerald-500"}`}>
              {data.environment === "preview" ? "⚠ PREVIEW" : "PRODUCTION"}
            </span>
          )}
          {data && !clean && (
            <div className="flex gap-1.5" data-testid="anomaly-severity-chips">
              {["high", "medium", "low"].map(s => (data.by_severity?.[s] || 0) > 0 && (
                <span key={s} className={`category-tag px-2 py-0.5 rounded-full ${SEV[s].badge}`}>{s} {data.by_severity[s]}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="anomaly-alerts-toggle" onClick={toggleAlerts} disabled={toggling || !data}
            title={alertsOn ? `Enabled by ${data?.alerts?.updated_by || "admin"} — high anomalies forwarded to MC/Discord` : "High anomalies stay in this panel only"}
            className={`px-3 py-1.5 rounded-full category-tag flex items-center gap-1.5 transition-colors disabled:opacity-50 ${
              alertsOn ? "bg-emerald-500/12 text-emerald-500" : dm ? "bg-white/5 text-slate-400" : "bg-black/5 text-[#6b7a7d]"}`}>
            {alertsOn ? <BellRing className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
            MC/Discord alerts {alertsOn ? "ON" : "OFF"}
          </button>
          <button data-testid="anomaly-refresh-btn" onClick={load} disabled={loading}
            className={`px-3 py-1.5 rounded-full category-tag flex items-center gap-1.5 disabled:opacity-50 ${dm ? "bg-white/5 text-slate-300" : "bg-black/5 text-[#3c4a4d]"}`}>
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />Re-scan
          </button>
        </div>
      </div>

      {clean ? (
        <div data-testid="anomaly-clean" className={`rounded-xl px-4 py-6 text-center ${dm ? "bg-[#141a1b]" : "bg-[#fafbfb]"}`}>
          <p className="text-sm font-semibold text-emerald-500">No anomalies detected</p>
          <p className={`text-xs mt-1 ${dm ? "text-slate-500" : "text-[#8a999c]"}`}>All rules passed: jobs, durations, scrape yield, error rates, Mongo network, integrations.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="anomaly-list">
          {anomalies.map((a, i) => (
            <div key={i} data-testid={`anomaly-item-${i}`} className={`flex gap-3 rounded-xl px-4 py-3 ${dm ? "bg-[#141a1b]" : "bg-[#fafbfb]"}`}>
              <span className={`w-1 rounded-full flex-shrink-0 ${SEV[a.severity]?.bar || "bg-slate-400"}`} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{a.title}</p>
                  <span className={`category-tag px-1.5 py-0.5 rounded-full ${SEV[a.severity]?.badge}`}>{a.severity}</span>
                  <span className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{a.source}</span>
                </div>
                <p className={`text-xs mt-0.5 break-words ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>{a.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
